import { useGameStore, AIAtMarker } from '../stores/gameStore'
import { networkClient } from '../network/client'

const BUILD_VERSION = 'v1.0.6'

export default function HUD() {
  const { timer, stability, squad, players, mySessionId, selectedRole, aiAtMarkers } = useGameStore()

  // Get my score
  const myPlayer = mySessionId ? players[mySessionId] : null
  const myScore = myPlayer?.score ?? 0

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const playerList = Object.values(players)

  return (
    <div className="absolute inset-x-0 top-0 p-4 pointer-events-none z-40">
      <div className="flex items-start justify-between">
        {/* Left: Timer and stability */}
        <div className="bg-gray-900/80 rounded-lg p-3 pointer-events-auto">
          <div className="flex items-center gap-4">
            {/* Timer */}
            <div className="text-center">
              <div className="text-xs text-gray-400">Czas</div>
              <div className="text-2xl font-mono text-emerald-neon">
                {formatTime(timer)}
              </div>
            </div>

            {/* Stability bar */}
            <div className="w-32">
              <div className="text-xs text-gray-400 mb-1">Stabilnosc</div>
              <div className="h-3 bg-gray-700 rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all duration-500 ${
                    stability > 50 ? 'bg-emerald-neon' : stability > 25 ? 'bg-yellow-500' : 'bg-red-500'
                  }`}
                  style={{ width: `${stability}%` }}
                />
              </div>
              <div className="text-xs text-right text-gray-500 mt-0.5">
                {stability}%
              </div>
            </div>

            {/* Squad type */}
            <div className="text-center">
              <div className="text-xs text-gray-400">Sklad</div>
              <div className="text-sm text-white capitalize">{squad}</div>
            </div>

            {/* Score */}
            <div className="text-center border-l border-gray-600 pl-4">
              <div className="text-xs text-gray-400">Punkty</div>
              <div className="text-xl font-bold text-yellow-400">{myScore}</div>
            </div>
          </div>
        </div>

        {/* Center: Role indicator */}
        <div
          className="bg-gray-900/80 rounded-lg px-4 py-2 pointer-events-auto"
          style={{ borderColor: getRoleColor(selectedRole), borderWidth: 2 }}
        >
          <div className="text-xs text-gray-400">Twoja rola</div>
          <div className="font-bold" style={{ color: getRoleColor(selectedRole) }}>
            {selectedRole.toUpperCase()}
          </div>
        </div>

        {/* Right: Players */}
        <div className="bg-gray-900/80 rounded-lg p-3 pointer-events-auto">
          <div className="text-xs text-gray-400 mb-2">Gracze</div>
          <div className="flex gap-2">
            {playerList.map((player) => (
              <div
                key={player.sessionId}
                className={`w-8 h-8 rounded flex items-center justify-center font-bold text-xs
                  ${player.sessionId === mySessionId ? 'ring-2 ring-white' : ''}`}
                style={{ backgroundColor: getRoleColor(player.role) }}
                title={`${player.nickname} (${player.role.toUpperCase()})`}
              >
                {player.role.charAt(0).toUpperCase()}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom left: Controls hint */}
      <div className="absolute bottom-4 left-4 bg-gray-900/60 rounded px-3 py-2 text-xs text-gray-400">
        WASD - ruch | 1/2/3 - wybierz AI | Prawy klik - polecenia AI
      </div>

      {/* Version number */}
      <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-red-600 text-white px-3 py-1 rounded text-xs font-bold z-50">
        {BUILD_VERSION}
      </div>

      {/* Bottom right: AI companions at markers */}
      {aiAtMarkers.length > 0 && (
        <div className="absolute bottom-4 right-4 bg-gray-900/90 rounded-lg p-3 pointer-events-auto">
          <div className="text-xs text-emerald-400 mb-2 font-semibold">🤖 AI przy markerach</div>
          {aiAtMarkers.map((ai) => (
            <AIMarkerButton key={`${ai.aiId}-${ai.markerId}`} ai={ai} players={players} />
          ))}
        </div>
      )}

      {/* AI Companions list */}
      {getAIPlayers().length > 0 && (
        <div className="absolute top-20 right-4 bg-gray-900/80 rounded-lg p-3 pointer-events-auto">
          <div className="text-xs text-gray-400 mb-2">AI Kompani (1/2/3)</div>
          <div className="space-y-1">
            {getAIPlayers().map((ai, index) => (
              <div
                key={ai.sessionId}
                className="flex items-center gap-2 text-xs"
              >
                <span className="text-gray-500 font-mono">[{index + 1}]</span>
                <div
                  className="w-4 h-4 rounded"
                  style={{ backgroundColor: getRoleColor(ai.role) }}
                />
                <span style={{ color: getRoleColor(ai.role) }}>
                  {ai.role.toUpperCase()}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )

  function getAIPlayers() {
    return Object.values(players).filter(p => p.isAI)
  }
}

function getRoleColor(role: string): string {
  const colors: Record<string, string> = {
    flux: '#00FF66',
    logic: '#4488FF',
    spark: '#FF8844',
    echo: '#AA44FF',
  }
  return colors[role] || '#ffffff'
}

// Marker metadata (should match GameScene)
const MARKER_DATA: Record<string, { type: string; difficulty: string }> = {
  puzzle_1: { type: 'pattern', difficulty: 'easy' },
  puzzle_2: { type: 'sequence', difficulty: 'easy' },
  puzzle_3: { type: 'logic', difficulty: 'easy' },
  puzzle_4: { type: 'debug', difficulty: 'easy' },
  puzzle_5: { type: 'pattern', difficulty: 'medium' },
  puzzle_6: { type: 'sequence', difficulty: 'medium' },
  puzzle_7: { type: 'logic', difficulty: 'medium' },
  puzzle_8: { type: 'debug', difficulty: 'hard' },
}

const PUZZLE_TYPE_ICONS: Record<string, string> = {
  pattern: '◈',
  sequence: '▤',
  logic: '⚡',
  debug: '⚠',
}

function AIMarkerButton({ ai, players }: { ai: AIAtMarker; players: Record<string, any> }) {
  const aiPlayer = Object.values(players).find(p => p.sessionId === ai.aiId || (p.isAI && p.role === ai.role))
  const markerInfo = MARKER_DATA[ai.markerId] || { type: 'pattern', difficulty: 'easy' }
  const icon = PUZZLE_TYPE_ICONS[markerInfo.type] || '?'

  const handleOpenPuzzle = () => {
    const room = networkClient.getGameRoom()
    if (room) {
      room.send('request_puzzle_via_ai', {
        aiId: ai.aiId,
        markerId: ai.markerId,
        difficulty: markerInfo.difficulty,
        type: markerInfo.type,
      })
    }
  }

  return (
    <button
      onClick={handleOpenPuzzle}
      className="flex items-center gap-2 w-full px-3 py-2 rounded bg-gray-800 hover:bg-emerald-900/50 transition-colors border border-gray-700 hover:border-emerald-500"
    >
      <div
        className="w-5 h-5 rounded flex items-center justify-center text-xs"
        style={{ backgroundColor: getRoleColor(ai.role) }}
      >
        {ai.role.charAt(0).toUpperCase()}
      </div>
      <span className="text-white text-xs">{aiPlayer?.nickname || ai.role.toUpperCase()}</span>
      <span className="text-gray-500 text-xs">→</span>
      <span className="text-emerald-400 text-xs">{icon} {markerInfo.type}</span>
      <span className="text-xs text-emerald-300 ml-auto">Otwórz</span>
    </button>
  )
}
